'use client'

import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Reveal } from '@/components/site/reveal'
import { SectionHead } from '@/components/site/section-head'
import { SiteSection } from '@/components/site/section'
import { OUTPUT_TABS, type OutputTabId } from './meeting-outputs-sample'
import { useDemoStarted } from '@/lib/analytics/use-demo-started'

export function MeetingOutputsSection() {
  const { t } = useTranslation(['web'])
  const [activeTab, setActiveTab] = useState<OutputTabId>('team')
  const markDemoStarted = useDemoStarted('meeting_outputs')
  const refs = useRef<Array<HTMLButtonElement | null>>([])
  const active = OUTPUT_TABS.find((tab) => tab.id === activeTab)!
  const choose = (index: number) => {
    markDemoStarted()
    setActiveTab(OUTPUT_TABS[index].id)
    refs.current[index]?.focus()
  }
  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number | null = null
    if (event.key === 'ArrowRight') next = (index + 1) % OUTPUT_TABS.length
    if (event.key === 'ArrowLeft') next = (index + OUTPUT_TABS.length - 1) % OUTPUT_TABS.length
    if (event.key === 'Home') next = 0
    if (event.key === 'End') next = OUTPUT_TABS.length - 1
    if (next !== null) {
      event.preventDefault()
      choose(next)
    }
  }
  return (
    <SiteSection id="sample" trackingKey="outputs">
      <Reveal>
        <SectionHead
          align="center"
          eyebrow={t('web:meetingNotesPage.outputs.eyebrow')}
          titleKey="web:meetingNotesPage.outputs.title"
          titleLeading="roomy"
          lead={t('web:meetingNotesPage.outputs.lead')}
        />
      </Reveal>
      <Reveal className="mt-(--gap-xl)">
        <div
          role="tablist"
          aria-label={t('web:meetingNotesPage.outputs.tabsLabel')}
          className="mx-auto flex max-w-[760px] flex-wrap gap-1 border-b border-border"
        >
          {OUTPUT_TABS.map((tab, index) => (
            <button
              key={tab.id}
              ref={(node) => {
                refs.current[index] = node
              }}
              id={`meeting-output-${tab.id}`}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls="meeting-output-panel"
              tabIndex={activeTab === tab.id ? 0 : -1}
              onClick={() => {
                markDemoStarted()
                setActiveTab(tab.id)
              }}
              onKeyDown={(event) => onKeyDown(event, index)}
              className="mb-[-1px] rounded-t-md border-b-2 border-transparent px-[14px] py-3 text-md font-medium hover:bg-fg-soft focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foreground aria-selected:border-foreground aria-selected:font-semibold"
            >
              {t(`web:meetingNotesPage.outputs.tabs.${tab.labelKey}`)}
            </button>
          ))}
        </div>
        <article
          id="meeting-output-panel"
          role="tabpanel"
          aria-labelledby={`meeting-output-${active.id}`}
          tabIndex={0}
          className="mx-auto mt-(--gap-lg) max-w-[760px] rounded-xl border border-border px-[clamp(22px,4vw,52px)] pt-[clamp(24px,3.4vw,44px)] pb-[clamp(28px,3.6vw,48px)] shadow-(--shadow) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground"
        >
          <p className="font-mono text-xs text-muted">
            {t('web:meetingNotesPage.outputs.usingTemplate', {
              template: t(`web:meetingNotesPage.outputs.tabs.${active.labelKey}`),
            })}
          </p>
          {activeTab === 'team' ? (
            <TeamDocument />
          ) : activeTab === 'partner' ? (
            <PartnerDocument />
          ) : (
            <SocialDocument />
          )}
        </article>
        <p className="mt-(--gap-md) text-center font-mono text-(length:--fs-meta) text-muted">
          {t('web:meetingNotesPage.outputs.caption')}
        </p>
      </Reveal>
    </SiteSection>
  )
}

function TeamDocument() {
  const { t } = useTranslation(['web'])
  return (
    <div className="mt-(--gap-lg)">
      <h3
        data-display-font=""
        className="font-display text-[length:clamp(24px,2.6vw,32px)] leading-[1.25] font-semibold"
      >
        {t('web:meetingNotesPage.outputs.team.title')}
      </h3>
      <DocumentHeading name="confirmed" />
      <ul className="mt-3 list-disc space-y-2 pl-5 text-md leading-[1.7]">
        <li>{t('web:meetingNotesPage.outputs.team.confirmedOne')}</li>
        <li>{t('web:meetingNotesPage.outputs.team.confirmedTwo')}</li>
        <li>{t('web:meetingNotesPage.outputs.team.confirmedThree')}</li>
      </ul>
      <DocumentHeading name="next" />
      <table className="mt-3 w-full text-left text-sm leading-[1.6]">
        <thead className="border-y border-border">
          <tr>
            <th className="py-[9px] pr-3">{t('web:meetingNotesPage.outputs.team.task')}</th>
            <th className="py-[9px] pr-3 whitespace-nowrap">
              {t('web:meetingNotesPage.outputs.team.owner')}
            </th>
            <th className="py-[9px] whitespace-nowrap">
              {t('web:meetingNotesPage.outputs.team.due')}
            </th>
          </tr>
        </thead>
        <tbody>
          {['one', 'two', 'three', 'four'].map((id) => (
            <tr key={id} className="border-b border-border">
              <td className="py-[9px] pr-3 align-top">
                {t(`web:meetingNotesPage.outputs.team.rows.${id}.task`)}
              </td>
              <td className="py-[9px] pr-3 align-top text-sm whitespace-nowrap text-muted">
                {t(`web:meetingNotesPage.outputs.team.rows.${id}.owner`)}
              </td>
              <td className="py-[9px] align-top text-sm text-muted">
                {t(`web:meetingNotesPage.outputs.team.rows.${id}.due`)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <DocumentHeading name="confirm" />
      <p className="mt-3 text-md leading-[1.7]">
        {t('web:meetingNotesPage.outputs.team.confirmText')}
      </p>
    </div>
  )
}
function PartnerDocument() {
  const { t } = useTranslation(['web'])
  return (
    <div className="mt-(--gap-lg) max-w-[46em]">
      <p className="border-b border-border pb-4 text-md">
        <span className="mr-3 text-xs text-muted">
          {t('web:meetingNotesPage.outputs.partner.subjectLabel')}
        </span>
        <strong>{t('web:meetingNotesPage.outputs.partner.subject')}</strong>
      </p>
      {['hello', 'intro', 'needs', 'ask', 'sign'].map((id) => (
        <p key={id} className="mt-4 text-md leading-[1.8] whitespace-pre-line">
          {t(`web:meetingNotesPage.outputs.partner.${id}`)}
        </p>
      ))}
    </div>
  )
}
function SocialDocument() {
  const { t } = useTranslation(['web'])
  return (
    <div className="mt-(--gap-lg) max-w-[34em]">
      <h3 className="text-lg font-semibold">{t('web:meetingNotesPage.outputs.social.title')}</h3>
      {['one', 'two', 'list', 'end'].map((id) => (
        <p key={id} className="mt-4 text-md leading-[1.8] whitespace-pre-line">
          {t(`web:meetingNotesPage.outputs.social.${id}`)}
        </p>
      ))}
    </div>
  )
}
function DocumentHeading({ name }: { name: string }) {
  const { t } = useTranslation(['web'])
  return (
    <h4 className="mt-(--gap-lg) border-t border-line-soft pt-[18px] font-semibold">
      {t(`web:meetingNotesPage.outputs.team.${name}`)}
    </h4>
  )
}
